import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { eur, num, shortDate, showMoney } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CustomerRecommendations } from "@/components/CustomerRecommendations";
import { CustomerMeasurements } from "@/components/CustomerMeasurements";
import { FitAdvisor } from "@/components/advisor/FitAdvisor";
import { EntityHistory } from "@/components/EntityHistory";
import { OtherAccounts } from "@/components/OtherAccounts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mail, Phone, MapPin, Tag, ChevronDown, Ruler, Palette, Shirt } from "lucide-react";

const MARKETING = { subscribed: "Iscritta", not_subscribed: "Non iscritta", unsubscribed: "Disiscritta", pending: "In attesa" };

const Box = ({ label, value, testid }) => (
  <div className="rounded-md border border-border/60 p-3">
    <p className="font-display text-lg font-bold" data-testid={testid}>{value}</p>
    <p className="text-[11px] text-muted-foreground">{label}</p>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <p className="mb-2 text-[11px] uppercase tracking-widest text-muted-foreground">{title}</p>
    {children}
  </div>
);

const Prefs = ({ title, items, icon: Icon, testid }) => (
  <div className="rounded-md border border-primary/40 bg-primary/5 p-3" data-testid={testid}>
    <p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-primary"><Icon className="h-3.5 w-3.5" />{title}</p>
    {items.length ? items.map((i, n) => (
      <p key={i.name} className={n === 0 ? "mb-1 break-words font-display text-xl font-bold leading-tight" : "text-sm"}>
        {i.name} <span className={`text-xs font-normal text-muted-foreground ${n === 0 ? "block" : ""}`}>{n === 0 ? "" : "· "}{num(i.value)} pz</span>
      </p>
    )) : <p className="text-sm text-muted-foreground">-</p>}
  </div>
);

const Tags = ({ tags }) => {
  const [all, setAll] = useState(false);
  const shown = all ? tags : tags.slice(0, 6);
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="customer-tags"><Tag className="h-3.5 w-3.5 text-muted-foreground" />
      {shown.map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
      {tags.length > 6 && <button type="button" onClick={() => setAll(!all)} className="text-[11px] text-primary hover:underline" data-testid="customer-tags-toggle">{all ? "mostra meno" : `+${tags.length - 6} altri`}</button>}
    </div>
  );
};

const Contacts = ({ c }) => (
  <div className="space-y-1.5 text-sm">
    {c.email && <p className="flex items-center gap-2 break-all"><Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{c.email}</p>}
    {c.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{c.phone}</p>}
    {c.addresses?.filter((a) => a.default).concat(c.addresses?.length && !c.addresses.some((a) => a.default) ? [c.addresses[0]] : []).map((a, i) => (
      <p key={i} className="flex items-start gap-2"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        {[a.address1, a.address2, [a.zip, a.city, a.province].filter(Boolean).join(" "), a.country].filter(Boolean).join(", ")}</p>
    ))}
    <p className="text-xs text-muted-foreground">
      Email marketing: {MARKETING[c.email_marketing] || "-"} · SMS: {MARKETING[c.sms_marketing] || "-"} · Cliente dal {shortDate(c.created_at)}
    </p>
    {c.tags?.length > 0 && <Tags tags={c.tags} />}
    {c.note && <p className="rounded bg-muted/50 p-2 text-xs">{c.note}</p>}
  </div>
);

const ContactsMenu = ({ c }) => (
  <Popover>
    <PopoverTrigger className="flex items-center gap-1.5 rounded-md border border-border/60 px-3 py-1.5 text-sm transition-colors hover:bg-secondary/40" data-testid="customer-contacts-toggle">
      <Mail className="h-3.5 w-3.5 text-muted-foreground" />Contatti, indirizzo e tag<ChevronDown className="h-4 w-4 text-muted-foreground" />
    </PopoverTrigger>
    <PopoverContent align="start" className="w-[min(92vw,420px)]" data-testid="customer-contacts"><Contacts c={c} /></PopoverContent>
  </Popover>
);

const SpentBox = ({ c, ins }) => {
  const [mode, setMode] = useState("anno");
  const year = new Date().getFullYear();
  const value = mode === "anno" ? ins.spent_2026 : c.season_spent;
  return (
    <div className="rounded-md border border-border/60 p-3" data-testid="customer-spent-period">
      <p className="font-display text-lg font-bold" data-testid="customer-spent-period-value">{eur(value)}</p>
      <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <span>Speso nel</span>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="h-6 w-auto gap-1 px-2 text-[11px]" data-testid="customer-spent-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="anno" data-testid="customer-spent-anno">{year}</SelectItem>
            <SelectItem value="stagione" data-testid="customer-spent-stagione">{c.current_season}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="mt-0.5 text-[10px] text-muted-foreground">{mode === "anno" ? "netto resi, tutti i capi" : "netto resi, solo capi della collezione"}</p>
    </div>
  );
};

const Kpis = ({ c, ins }) => (
  <div className="grid grid-cols-3 gap-2 lg:grid-cols-6">
    {showMoney() && <Box label="Speso in totale (Shopify)" value={eur(c.total_spent)} testid="customer-spent" />}
    <Box label="Ordini totali" value={num(c.orders_count)} />
    {showMoney() && <SpentBox c={c} ins={ins} />}
    <Box label="Capi acquistati dal 2026" value={num(ins.pieces)} />
    <Box label="Capi resi" value={num(ins.returned)} />
    <Box label="Tasso di reso" value={`${num(ins.return_rate)}%`} testid="customer-return-rate" />
  </div>
);

const Orders = ({ orders }) => {
  const navigate = useNavigate();
  const cancelled = orders.filter((o) => o.cancelled).length;
  return (
    <Section title={`Ordini dal 2026 (${orders.length - cancelled}${cancelled ? ` + ${cancelled} annullati` : ""})`}>
      <div className="max-h-[420px] space-y-1.5 overflow-y-auto pr-1">
        {orders.map((o) => (
          <button type="button" key={o.id} onClick={() => navigate(`/ordini?ordine=${encodeURIComponent(o.order_number)}`)} title="Apri il dettaglio nel pannello Ordini"
            className={`flex w-full items-center justify-between rounded border border-border/60 px-3 py-2 text-left text-sm transition-colors hover:border-primary/50 hover:bg-secondary/40 ${o.cancelled ? "opacity-60" : ""}`} data-testid={`customer-order-${o.order_number}`}>
            <span><span className="font-mono-erp">{o.order_number}</span> <span className="text-muted-foreground">· {shortDate(o.date)} · {o.pieces} pz</span>
              {o.cancelled && <Badge variant="outline" className="ml-2 border-rose-500/50 text-[10px] text-rose-400" data-testid={`customer-order-cancelled-${o.order_number}`}>Annullato</Badge>}
              {o.returned_pieces > 0 && <Badge variant="outline" className="ml-2 border-amber-500/50 bg-amber-500/10 text-[10px] text-amber-400" data-testid={`customer-order-returned-${o.order_number}`}>Reso · {o.returned_pieces} pz</Badge>}</span>
            {showMoney() && <span className={`text-right ${o.cancelled ? "line-through" : ""}`}>
              {eur(o.total)}
              {o.returned_amount > 0 && <span className="block text-[11px] text-rose-400">reso {eur(o.returned_amount)}</span>}
            </span>}
          </button>
        ))}
        {orders.length === 0 && <p className="text-sm text-muted-foreground">Nessun ordine dal 1° gennaio 2026.</p>}
      </div>
    </Section>
  );
};

const Profile = ({ c, ins, onSelect }) => (
  <div className="space-y-5">
    <OtherAccounts data={c.other_accounts} onSelect={onSelect} />
    <div className="space-y-2">
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Cosa compra di più</p>
      <div className="grid grid-cols-3 gap-2">
        <Prefs title="Taglie" items={ins.sizes} icon={Ruler} testid="customer-pref-sizes" />
        <Prefs title="Colori" items={ins.colors} icon={Palette} testid="customer-pref-colors" />
        <Prefs title="Categorie" items={ins.categories} icon={Shirt} testid="customer-pref-categories" />
      </div>
    </div>
    <Orders orders={c.orders} />
  </div>
);

const Work = ({ c, id }) => (
  <Tabs defaultValue="taglie">
    <TabsList className="mb-3 flex h-auto flex-wrap">
      <TabsTrigger value="taglie" data-testid="customer-tab-taglie">Misure e capi consigliati</TabsTrigger>
      <TabsTrigger value="complementari" data-testid="customer-tab-complementari">Capi complementari</TabsTrigger>
      <TabsTrigger value="storico" data-testid="customer-tab-storico">Storico modifiche</TabsTrigger>
    </TabsList>
    <TabsContent value="taglie" className="mt-0">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <div className="xl:col-span-2"><CustomerMeasurements id={id} value={c.measurements} /></div>
        <div className="xl:col-span-3"><FitAdvisor customer={c} /></div>
      </div>
    </TabsContent>
    <TabsContent value="complementari" className="mt-0"><CustomerRecommendations id={id} /></TabsContent>
    <TabsContent value="storico" className="mt-0"><EntityHistory entityId={id} testid="customer-history" /></TabsContent>
  </Tabs>
);

export const CustomerSheet = ({ id, onClose, onSelect }) => {
  const { data: c, isLoading } = useQuery({ queryKey: ["customer", id], enabled: !!id, queryFn: async () => (await api.get(`/customers/${id}`)).data });
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-[92vw] xl:max-w-[1400px]" data-testid="customer-sheet">
        {isLoading || !c ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
          <>
            <SheetHeader className="flex-row flex-wrap items-center gap-3 space-y-0 pr-8">
              <SheetTitle data-testid="customer-name">{c.name}</SheetTitle>
              <ContactsMenu c={c} />
            </SheetHeader>
            <div className="mt-4 space-y-5">
              <Kpis c={c} ins={c.insights} />
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="order-2 lg:order-1 lg:border-r lg:border-border/60 lg:pr-6"><Profile c={c} ins={c.insights} onSelect={onSelect} /></div>
                <div className="order-1 lg:order-2 lg:col-span-2"><Work c={c} id={id} /></div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
